import { useCallback, useEffect, useRef } from "react";

import { LocalStorageConfigs, loadFromLocalWithFallback, saveToLocal } from "../../../utils/LocalStorage";
import { useObx } from "../../../utils/UseObx";
import ApiRequestPaths from "../../Constants/ApiRequestPaths";
import { CreatePatientExperimentHook } from "../Components/ExperimentAssignmentAppContextProvider";

export interface IPatientExperimentAssignment extends Record<string, unknown> {
  /** The id of the patient being assigned to the experiment. */
  patientId: string;
  /** The id of the experiment to which the patient is being assigned. */
  experimentId: string;
  /** A flag indicating if the patient is assigned to the associated experiment. */
  isAssigned: boolean;
}

/** A hook which reports patient registrations for experiments to the server.
 * @remarks Caches successful reports so they are never sent a second time.
 */
const useCreatePatientExperimentManualAuth: CreatePatientExperimentHook = ({ bearerAuthToken, contactId }) => {
  const reportedPatientExperiments = useRef(
    (
      loadFromLocalWithFallback<Array<IPatientExperimentAssignment & { customerId?: string }>>(
        LocalStorageConfigs.reportedPatientExperiments.key,
        LocalStorageConfigs.legacy.reportedPatientExperiments.key
      ) || []
    ).map(exp => (exp.patientId ? exp : { ...exp, patientId: exp.customerId ?? "" }))
  );
  const { res, err, loading, setReq } = useObx<unknown, { isAssigned: boolean }, IPatientExperimentAssignment>(
    "createPatientExperiment",
    {
      method: "post",
      config: { headers: { authorization: `Bearer ${bearerAuthToken}` } }
    }
  );

  const execute = useCallback((patientExperimentAssignment: IPatientExperimentAssignment) => {
    if (!reportedPatientExperiments.current.some(exp => exp.patientId === patientExperimentAssignment.patientId))
      setReq(
        r =>
          r && {
            ...r,
            url: ApiRequestPaths.postPatientExperiment(
              contactId,
              patientExperimentAssignment.patientId,
              patientExperimentAssignment.experimentId
            ),
            payload: { isAssigned: patientExperimentAssignment.isAssigned },
            custom: patientExperimentAssignment
          }
      );
  }, []);

  useEffect(() => {
    if (!res?.request.payload) return;
    reportedPatientExperiments.current.push(res.request.custom!);
    saveToLocal(reportedPatientExperiments.current, LocalStorageConfigs.reportedPatientExperiments.key);
  }, [res]);

  return { result: null, error: err === undefined ? null : Boolean(err), isLoading: loading, execute };
};

export default useCreatePatientExperimentManualAuth;
